export function generateReference(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const random = Math.floor(1000 + Math.random() * 9000);
  return `IZP-${year}${month}${day}-${random}`;
}

export function generateValidationCode(digits = 6): string {
  let code = '';
  for (let index = 0; index < digits; index += 1) {
    code += Math.floor(Math.random() * 10);
  }
  return code;
}

export function generateId(): string {
  return `req-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

/**
 * Identifiant de connexion bancaire dérivé du code d'identification client.
 * L'administrateur peut le modifier avant transmission.
 */
export function generateBankLogin(accessCode: string): string {
  return `IZI-${accessCode}`;
}

/**
 * Mot de passe aléatoire lisible (sans caractères ambigus 0/O/1/I).
 */
export function generateBankPassword(length = 10): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let value = '';
  for (let index = 0; index < length; index += 1) {
    value += chars[Math.floor(Math.random() * chars.length)];
  }
  return value;
}

export type CardBrand = 'Visa' | 'Mastercard' | 'UnionPay';

/** Marques de carte proposées pour la génération et la validation. */
export const CARD_BRANDS: CardBrand[] = ['Visa', 'Mastercard', 'UnionPay'];

export type SimulatedCard = {
  brand: CardBrand;
  number: string;
  expiry: string;
  cvv: string;
};

/** Calcule la clé de contrôle de Luhn d'un numéro de carte incomplet. */
function luhnCheckDigit(partial: string): string {
  let sum = 0;
  let alternate = true;
  for (let index = partial.length - 1; index >= 0; index -= 1) {
    let digit = Number(partial[index]);
    if (alternate) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    alternate = !alternate;
  }
  return String((10 - (sum % 10)) % 10);
}

/**
 * Génère une carte simulée cohérente (préfixe de la marque, 16 chiffres,
 * clé de Luhn valide, expiration future, CVV). Si aucune marque n'est
 * fournie, elle est choisie aléatoirement parmi Visa, Mastercard et UnionPay.
 */
export function generateSimulatedCard(brandInput?: CardBrand): SimulatedCard {
  const brand: CardBrand =
    brandInput ?? CARD_BRANDS[Math.floor(Math.random() * CARD_BRANDS.length)];
  const prefix = brand === 'Visa' ? '4' : brand === 'Mastercard' ? '5' : '62';

  let body = prefix;
  while (body.length < 15) {
    body += String(Math.floor(Math.random() * 10));
  }
  const number = body + luhnCheckDigit(body);

  const now = new Date();
  const month = String(1 + Math.floor(Math.random() * 12)).padStart(2, '0');
  const year = String((now.getFullYear() + 1 + Math.floor(Math.random() * 4)) % 100).padStart(2, '0');
  const cvv = String(Math.floor(100 + Math.random() * 900));

  return { brand, number, expiry: `${month}/${year}`, cvv };
}

/** Affiche un numéro de carte par groupes de 4 chiffres. */
export function formatCardNumber(number: string): string {
  return (number || '').replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
}

/** Ne conserve que les 8 derniers chiffres d'un numéro de carte. */
export function cardLast8(number: string): string {
  return (number || '').replace(/\s/g, '').slice(-8);
}